import React, {useMemo, useCallback, useState, useRef, useEffect} from "react";
import {
  createEditor,
  Descendant, Editor,
  Transforms,
} from "slate";
import {Slate, Editable, withReact, RenderElementProps, useSelected, useFocused, ReactEditor} from "slate-react";
import { withHistory } from "slate-history";
import { WorkspaceMember } from "../../lib/types";
import isHotkey from "is-hotkey";

// Plugins
import { withMarkdownShortcuts } from "./plugins/markdown-shortcuts";
import {withMentions, insertMention, MentionElementType, MentionToolbar} from "./plugins/mentions";

import { withPasteHtml } from "./plugins/paste-html";
import { HoveringToolbar } from "./plugins/hovering-toolbar";
import {Button} from "../common/Button";
import {MentionProvider, useMentionContext} from "./plugins/mentionProvider";
import {useUserContext} from "../../providers/UserContext";
import {renderElement, renderLeaf, slateToPlainText} from "./PreviewRenderer";
import {Icon} from "../common/Icon";
import classNames from "classnames";
import {uploadBytes} from "firebase/storage";
import {UploadImages} from "../../lib/hooks/UploadImages";
import {CustomElement} from "../../types/slate";
import {useLayoutContext} from "../../providers/LayoutProvider";

type MentionElement = {
  type: "mention";
  user: WorkspaceMember;
  children: Descendant[];
};

export interface MessageComposerProps {
  onSend: (editor: ReactEditor) => void;
  onType?: (editor: ReactEditor) => void;
  onReady?: (editor: ReactEditor) => void;
  minHeight?: number | "full";
  initialValue?: Descendant[];
  disabled?: boolean;
  hiddenToolbar?: boolean;
  hasCancelButton?: boolean;
  onCancel?: (editor: ReactEditor) => void;
  submitLabel?: string;
}


const allowedSchemes = ['http:', 'https:', 'mailto:', 'tel:']

const SafeLink = ({ attributes, children, href }: any) => {
  const safeHref = useMemo(() => {
    let parsedUrl = null
    try {
      parsedUrl = new URL(href)
      // eslint-disable-next-line no-empty
    } catch {}
    if (parsedUrl && allowedSchemes.includes(parsedUrl.protocol)) {
      return parsedUrl.href
    }
    return 'about:blank'
  }, [href])
  return (
    <a href={safeHref} {...attributes} className={"text-blue-1 underline"}>
      {children}
    </a>
  )
}

const ImageElement = ({ attributes, children, element }: any) => {
  const selected = useSelected()
  const focused = useFocused()
  return (
    <div {...attributes}>
      {children}
      <img
        src={element.url}
        className="max-w-full h-20 shadow-elevation-1"
      />
    </div>
  )
}

export const SlateEditor: React.FC<MessageComposerProps> = (
  {
    disabled = false,
    onSend,
    onType,
    onReady,
    minHeight = 80,
    initialValue = [
      {
        type: "paragraph",
        children: [{ text: "" }],
      },
    ],
    hiddenToolbar = true,
    hasCancelButton = false,
    onCancel = () => {},
    submitLabel = "Send",
  }) => {
  const editor = useMemo(
    () =>
      withPasteHtml(
        withMarkdownShortcuts(withMentions(withReact(withHistory(createEditor()))))
      ),
    []
  );

  const {isMobileResolution, isTouchDevice} = useLayoutContext();
  const [isValid, setIsValid] = useState(false);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const canSend = (editor: ReactEditor) => {
    const hasContent = slateToPlainText(editor.children).trim().length > 0;
    const imagesLoaded = editor.children.every((node) => {
      node = node as CustomElement;
      return node.type !== 'image' || !node.loading;
    });
    return hasContent && imagesLoaded && !disabled;
  }
  const handleSend = (event: any) => {
    if (!canSend(editor)) {
      return;
    }
    event.nativeEvent.preventDefault();
    event.nativeEvent.stopImmediatePropagation();
    event.stopPropagation();
    onSend(editor);
    Transforms.delete(editor, {
      at: {
        anchor: Editor.start(editor, []),
        focus: Editor.end(editor, []),
      },
    });
    Transforms.select(editor, {offset: 0, path: [0, 0]});
    if (editorRef.current) {
      editorRef.current.focus();
    }
  };

  useEffect(() => {
    if(!isTouchDevice) {
      Transforms.select(editor, {offset: 0, path: [0, 0]});
      if (editorRef.current) {
        editorRef.current.focus();
      }
    }
    if(onReady) {
      onReady(editor || null);
    }
  }, [editor]);


  const {setSearchTerm, searchTerm, updateSearch, handleMentionKeyDown, mentionResults} = useMentionContext();
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (mentionResults.length > 0) {
      if (handleMentionKeyDown(editor, event)) {
        return; // Prevent further handling if mention logic consumes the event
      }
    }
    if (event.key === "Enter" && event.shiftKey===false && !isMobileResolution) {
      if (!canSend(editor)) {
        return;
      }
      // e.nativeEvent.stopImmediatePropagation()
      // e.preventDefault()
      // e.stopPropagation()
      event.nativeEvent.preventDefault();
      event.nativeEvent.stopImmediatePropagation();
      event.stopPropagation();
      onSend(editor)
      Transforms.delete(editor, {
        at: {
          anchor: Editor.start(editor, []),
          focus: Editor.end(editor, []),
        },
      });
      Transforms.select(editor, { offset: 0, path: [0, 0] });
    } else {
      if (event.key === "@") {
        setSearchTerm("");
      }
    }
  };

  const [focused, setFocused] = useState(false);

  return (
    <div className={
      classNames(
        "relative w-full bg-white lg:rounded-lg border-t lg:border-t-transparent lg:shadow-elevation-1 px-4 font-style-body-md",
        hiddenToolbar && (focused || !isMobileResolution) ? "py-4" : "py-2",
        minHeight === "full" ? "h-[100%] flex flex-col justify-between border-t-gray-8" : "h-auto border-t-gray-11 shadow-elevation-3"
      )
    }>
        <Slate
          editor={editor}
          initialValue={initialValue}
          onChange={() => {
            updateSearch(editor);
            setIsValid(canSend(editor));
          }}
        >
          <MentionToolbar />        <HoveringToolbar />
          <Editable
            ref={editorRef}
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            renderPlaceholder={({ attributes, children }) => (
              <div {...attributes} className={"absolute mt-2"}>
                {children}
              </div>
            )}
            placeholder={"Type a message"}
            onKeyDown={handleKeyDown}
            onKeyUp={() => {
              if (onType) {
                onType(editor);
              }
            }}
            className={classNames(
              "w-full p-2 overflow-y-auto font-style-body-md overflow-x-hidden focus:border-gray-1 focus:outline-0 slate-editor",
              minHeight === "full" ? "flex-grow" : "!min-h-["+minHeight+"px] max-h-[400px]"
            )}
            spellCheck
            onFocus={() => {
              setFocused(true);
            }}
            onBlur={() => {
              window.setTimeout(
                () => {
                  if (editorRef.current && document.activeElement !== editorRef.current) {
                    setFocused(false);
                  }
                },
                100
              )

            }}
            autoFocus={!isTouchDevice}
          />
        </Slate>

        {searchTerm && mentionResults.length > 0 && (
          <MentionDropdown
            users={mentionResults}
            onSelect={(user) => {
              insertMention(editor, user);
            }}
          />
        )}

        <div className={
          classNames(
            "flex justify-between mt-2 overflow-hidden transition-all",
            !focused && isMobileResolution && hiddenToolbar ? "h-[0]" : "h-[40px]"
          )}>
          <div className="left">
            <Button className={classNames([
              "relative cursor-pointer",
            ])} icon="only" variant={"tertiary"}>
              <Icon name="image" size="small"/>
              <input
                className="absolute inset-0 w-full h-full opacity-0"
                accept="image/*"
                multiple={true}
                type="file" onChange={(e)=>{
                if (e.target.files) {
                  for(let i in e.target.files) {
                    if (!e.target.files[i].type) {
                      continue;
                    }
                    const file = e.target.files[i];
                    const localUrl = URL.createObjectURL(file);
                    const tempImage = {
                      type: "image",
                      url: localUrl,
                      loading: true,
                      children: [{text: ""}], // Ensure valid children
                    } as CustomElement;
                    const emptyParagraph = {type: 'paragraph', children: [{text: ""}]} as CustomElement

                    Transforms.insertNodes(editor, tempImage);
                    Transforms.insertNodes(editor, emptyParagraph);

                    UploadImages({
                        file: file,
                        callback: (url) => {
                          editor.children.forEach((node, index) => {
                            node = node as CustomElement;
                            if (node.type === 'image' && node.url === localUrl) {
                              Transforms.setNodes(
                                editor,
                                {url, loading:false},
                                {at: [index]} // Update the node directly using its path
                              );
                            }
                          });
                        }
                      }
                    )
                  }
                }
              }
              }/>
            </Button>

          </div>
          <div className="right flex gap-2">
            {hasCancelButton && (
              <Button
                variant={"outline"}
                onClick={() => {
                  onCancel(editor)
                }}
              >
                Cancel
              </Button>
            )
            }
            <Button
              onClick={handleSend}
              disabled={!isValid || disabled}
            >
              {submitLabel}
            </Button>
          </div>
        </div>
    </div>
  );
};

// Mention Element Renderer
const MentionElementComponent: React.FC<RenderElementProps> = ({
                                                                 attributes,
                                                                 children,
                                                                 element,
                                                               }) => {
  const mention = element as MentionElementType;
  return (
    <span {...attributes} className="bg-blue-100 text-blue-600 px-1 rounded">
            @{mention.userName}
      {children}
          </span>
  );
};

// Default Element Renderer
const DefaultElement: React.FC<RenderElementProps> = ({
                                                        attributes,
                                                        children,
                                                      }) => {
  return <p {...attributes}>{children}</p>;
};

// Mention Dropdown
interface MentionDropdownProps {
  users: WorkspaceMember[];
  onSelect: (user: WorkspaceMember) => void;
}

const MentionDropdown: React.FC<MentionDropdownProps> = ({ users, onSelect }) => {
  return (
    <div className="fixed left-0 bottom-full mb-2 bg-white shadow-md rounded-md w-56 z-10">
      {users.map((user) => (
        <div
          key={user.id}
          className="p-2 hover:bg-gray-100 cursor-pointer flex items-center"
          onClick={() => onSelect(user)}
        >
          <img
            src={user.avatar}
            alt={user.displayName}
            className="w-6 h-6 rounded-full mr-2"
          />
          <span>{user.displayName}</span>
        </div>
      ))}
    </div>
  );
};

export const clearEditor = (editor: ReactEditor) => {
  // Define the default empty value for the editor
  const defaultValue: Descendant[] = [
    {
      type: "paragraph",
      children: [{ text: "" }],
    },
  ];

  // Remove all nodes from the editor
  Transforms.removeNodes(editor, { at: [0] });

  // Insert the default value
  Transforms.insertNodes(editor, defaultValue, { at: [0] });
  Transforms.select(editor, Editor.end(editor, []));
};
